import { Check, Phone, MessageSquare, Sparkles, CalendarClock } from "lucide-react";
import { contactInfo, getGlobalMinPrice } from "@/data/machines";
import Reveal, { Stagger, RevealItem } from "@/components/site/Reveal";

const perksDay = [
  "Entrega y recogida coordinadas",
  "Asesoramiento técnico incluido",
  "Máquina revisada y lista para trabajar",
];
const perksLong = [
  "Presupuesto adaptado a tu proyecto",
  "Mejor precio según duración",
  "Mantenimiento incluido durante el alquiler",
];

export default function Pricing() {
  const minPrice = getGlobalMinPrice();
  const waLink = `https://wa.me/${contactInfo.whatsapp}?text=${encodeURIComponent(
    "Hola, necesito un presupuesto para alquiler de larga duración."
  )}`;

  return (
    <section
      id="tarifas"
      data-testid="pricing-section"
      className="py-24 md:py-28 bg-white relative overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute top-24 -left-24 w-[24rem] h-[24rem] rounded-full bg-sky-100/60 blur-3xl pointer-events-none"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <Reveal>
          <div className="max-w-2xl">
            <span className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-sky-600">
              <span className="w-6 h-px bg-sky-500" /> Tarifas
            </span>
            <h2 className="font-heading mt-4 text-3xl md:text-4xl font-bold tracking-tight text-slate-950 leading-[1.1]">
              Tarifas claras, sin sorpresas
            </h2>
            <p className="mt-5 text-slate-600 text-base md:text-lg leading-relaxed">
              Elige el plan que mejor encaje con tu trabajo. Sin permanencia y sin letra pequeña.
            </p>
          </div>
        </Reveal>

        <Stagger className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 items-stretch">
          {/* Card 1 — Por día */}
          <RevealItem>
            <div
              data-testid="plan-dia"
              className="group relative h-full rounded-2xl p-8 md:p-10 border border-slate-200 bg-white hover:border-sky-300 hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300 flex flex-col"
            >
              <div className="flex items-center gap-2 text-sky-600 text-xs font-semibold uppercase tracking-wider">
                <CalendarClock className="w-4 h-4" />
                Corta duración
              </div>
              <h3 className="font-heading mt-4 text-2xl md:text-3xl font-bold tracking-tight text-slate-950">
                Alquiler por día
              </h3>

              <div className="mt-6 flex items-baseline gap-2">
                <span className="font-heading text-3xl md:text-4xl font-bold text-slate-950 leading-none">
                  Consulta tarifa
                </span>
              </div>

              <p className="mt-5 text-slate-600 text-base leading-relaxed">
                Perfecta para alquileres cortos, sustituciones puntuales o refuerzos de flota.
                Elige la máquina, dinos las fechas y la tienes lista en pocas horas.
              </p>

              <ul className="mt-7 space-y-3">
                {perksDay.map((perk) => (
                  <li key={perk} className="flex items-start gap-2.5 text-sm">
                    <Check
                      className="w-4 h-4 mt-0.5 shrink-0 text-sky-600 transition-transform duration-300 group-hover:scale-110"
                      strokeWidth={3}
                    />
                    <span className="text-slate-700">{perk}</span>
                  </li>
                ))}
              </ul>

              <a
                href={`tel:${contactInfo.phoneRaw}`}
                data-testid="plan-cta-dia"
                className="shine mt-auto pt-8 inline-flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-600 text-white font-semibold rounded-xl px-5 py-4 text-sm md:text-base transition-all duration-300 shadow-md shadow-sky-500/25 hover:-translate-y-0.5"
              >
                <Phone className="w-4 h-4 group-hover:wiggle" />
                Alquilar ahora
              </a>
            </div>
          </RevealItem>

          {/* Card 2 — Larga duración */}
          <RevealItem>
            <div
              data-testid="plan-larga"
              className="group relative h-full rounded-2xl p-8 md:p-10 border border-slate-200 bg-white hover:border-sky-300 hover:shadow-xl hover:-translate-y-1.5 transition-all duration-300 flex flex-col"
            >
              <span className="absolute -top-3 left-8 inline-flex items-center gap-1.5 bg-sky-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md shadow-sky-500/30">
                <Sparkles className="w-3 h-3" />
                Mejor precio
              </span>
              <div className="relative flex flex-col h-full">
                <div className="flex items-center gap-2 text-sky-600 text-xs font-semibold uppercase tracking-wider">
                  <CalendarClock className="w-4 h-4" />
                  Más de una semana
                </div>
                <h3 className="font-heading mt-4 text-2xl md:text-3xl font-bold tracking-tight text-slate-950">
                  Alquiler de larga duración
                </h3>

                <div className="mt-6">
                  <span className="font-heading text-3xl md:text-4xl font-bold leading-none text-slate-950">
                    Precio personalizado
                  </span>
                </div>

                <p className="mt-5 text-slate-600 text-base leading-relaxed">
                  Si necesitas la máquina durante más de una semana, te prepararemos un presupuesto
                  adaptado con el mejor precio según la duración del alquiler.
                </p>

                <ul className="mt-7 space-y-3">
                  {perksLong.map((perk) => (
                    <li key={perk} className="flex items-start gap-2.5 text-sm">
                      <Check
                        className="w-4 h-4 mt-0.5 shrink-0 text-sky-600 transition-transform duration-300 group-hover:scale-110"
                        strokeWidth={3}
                      />
                      <span className="text-slate-700">{perk}</span>
                    </li>
                  ))}
                </ul>

                <a
                  href={waLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="plan-cta-larga"
                  className="shine mt-auto pt-8 inline-flex items-center justify-center gap-2 bg-sky-500 hover:bg-sky-600 text-white font-semibold rounded-xl px-5 py-4 text-sm md:text-base transition-all duration-300 shadow-md shadow-sky-500/25 hover:-translate-y-0.5"
                >
                  <MessageSquare className="w-4 h-4" />
                  Solicitar presupuesto
                </a>
              </div>
            </div>
          </RevealItem>
        </Stagger>
        <Reveal delay={0.15}>
          <p className="mt-6 text-xs font-semibold uppercase tracking-wider text-slate-500">
            * Todos los precios mostrados no incluyen IVA.
          </p>
        </Reveal>
      </div>
    </section>
  );
}
